import { Column } from '@tanstack/react-table';
import { TextInput } from '@carbon/react';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { DataTableColumnMeta } from './types';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface HeaderTextFilterProps<TData, TValue> {
  column: Column<TData, TValue>;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Inline text filter for the table header filter row.
 * Displays a compact text input using Carbon TextInput.
 * Includes debouncing to prevent excessive filtering during input.
 * @param {HeaderTextFilterProps} props - Component props
 * @param {Column} props.column - TanStack Table column instance
 * @return {JSX.Element} The header text filter element
 */
export function HeaderTextFilter<TData, TValue>({
  column,
}: HeaderTextFilterProps<TData, TValue>) {
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const filterValue = (column.getFilterValue() as string) ?? '';
  const [localValue, setLocalValue] = useState(filterValue);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((value: string) => {
        column.setFilterValue(value || undefined);
      }, 300),
    [column]
  );

  // Cancel debounce on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  // Sync from external changes
  useEffect(() => {
    setLocalValue(filterValue);
  }, [filterValue]);

  return (
    <TextInput
      id={`filter-${column.id}`}
      labelText=""
      hideLabel
      size="sm"
      autoComplete={meta?.disableFilterAutocomplete ? 'off' : undefined}
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
        debouncedSetFilter(e.target.value);
      }}
      placeholder="Filter..."
    />
  );
}
